import * as React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { cn } from "../../utils/utils";
import { cva } from "class-variance-authority";

const KanbanContainerVariants = cva(
  "flex flex-col h-full rounded-lg border bg-background shadow-sm",
  {
    variants: {
      variant: {
        default: "border-border",
        dragging: "border-primary",
        todo: "border-blue-200 dark:border-blue-800/30",
        inProgress: "border-amber-200 dark:border-amber-800/30",
        done: "border-green-200 dark:border-green-800/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const KanbanHeaderVariants = cva(
  "flex items-center justify-between rounded-t-lg p-3 font-semibold",
  {
    variants: {
      variant: {
        default: "bg-muted/50",
        todo: "bg-blue-50 text-blue-800 dark:bg-blue-950/30 dark:text-blue-400",
        inProgress: "bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400",
        done: "bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface KanbanContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  title: string;
  count?: number;
  variant?: "default" | "todo" | "inProgress" | "done";
}

export const KanbanContainer = React.forwardRef<
  HTMLDivElement,
  KanbanContainerProps
>(({ id, title, children, variant, count = 0, className, ...props }, _ref) => {
  const { isOver, setNodeRef } = useDroppable({ id });
  
  const containerClassName = cn(
    KanbanContainerVariants({ 
      variant: isOver ? "dragging" : variant 
    }),
    className
  );
  
  return (
    <div ref={setNodeRef} className={containerClassName} {...props}>
      <div className={KanbanHeaderVariants({ variant })}>
        <span>{title}</span>
        <div className="rounded-full px-2 py-0.5 text-xs bg-background">{count}</div>
      </div>
      
      <div className="p-3 flex-1 overflow-auto space-y-3">
        {children}
      </div>
    </div>
  );
});

KanbanContainer.displayName = "KanbanContainer";

// Correction de l'interface KanbanItemProps avec un id personnalisé
export interface KanbanItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  id: string | number;  // Définir notre propre type id qui permet string ou number
  isDraggable?: boolean;
}

export const KanbanItem = React.forwardRef<
  HTMLDivElement,
  KanbanItemProps
>(({ id, children, isDraggable = true, className, ...props }, _ref) => {
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id,
    disabled: !isDraggable
  });
  
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition: isDragging ? 'none' : 'transform 120ms ease-in-out'
      }
    : undefined;
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-background border rounded-md p-3 shadow-sm",
        isDragging && "shadow-md z-10",
        !isDraggable && "cursor-default",
        isDraggable && "cursor-grab active:cursor-grabbing",
        className
      )}
      {...attributes}
      {...listeners}
      {...props}
    >
      {children}
    </div>
  );
});

KanbanItem.displayName = "KanbanItem";
